import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useFamilyMembers } from '../../hooks/useFamilyMembers';
import AddFamilyMemberModal from '../../components/account/AddFamilyMemberModal';
import {
  PlusIcon,
  UserIcon,
  UsersIcon,
  SmileIcon,
  CalendarIcon,
  FileTextIcon,
  AlertCircleIcon,
  InfoIcon,
} from '../../components/common/icons';

export default function FamilyOverview() {
  const { familyMembers, loading, loadError, refreshing, refresh } = useFamilyMembers();
  const [showAddMember, setShowAddMember] = useState(false);

  const getRelationshipIcon = (memberName) => {
    // Simple heuristic based on entity type and common patterns
    const name = memberName.toLowerCase();
    if (name.includes('barn') || name.includes('sønn') || name.includes('datter')) {
      return SmileIcon;
    }
    if (name.includes('mor') || name.includes('far') || name.includes('forelder')) {
      return UsersIcon;
    }
    return UserIcon;
  };

  const formatLastUpdated = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return 'I dag';
    } else if (diffDays === 1) {
      return 'I går';
    } else if (diffDays < 7) {
      return `${diffDays} dager siden`;
    } else {
      return date.toLocaleDateString('nb-NO');
    }
  };

  const getKeyInfo = (memories) => {
    const info = {};

    memories.forEach(memory => {
      if (memory.content.includes('år gammel') || memory.content.includes('født i')) {
        const ageMatch = memory.content.match(/(\d+)\s*år/);
        const birthMatch = memory.content.match(/født i (\d{4})/);
        if (ageMatch) info.age = ageMatch[1];
        if (birthMatch) info.birthYear = birthMatch[1];
      }

      if (memory.content.includes('diagnose')) {
        info.hasHealth = true;
      }

      if (memory.content.includes('utfordringer')) {
        info.hasChallenges = true;
      }
    });

    return info;
  };

  return (
    <div className="max-w-4xl">
      {/* Add Member Modal (shared with the dashboard FamilyCard) — hoisted
          above the loading branch so no loading/refresh state can ever unmount
          it mid-submit. refresh receives the new member's entityId from
          onAdded and retries until the Search index returns it. A name
          already in the family is not saved again. */}
      <AddFamilyMemberModal
        isOpen={showAddMember}
        onClose={() => setShowAddMember(false)}
        onAdded={refresh}
        existingNames={familyMembers.map((member) => member.name)}
      />

      {loading ? (
        <>
          {/* Initial-load skeleton — only the FIRST fetch renders this branch;
              refresh() keeps the live list mounted (see useFamilyMembers).
              Header skeleton mirrors the stacked live header (one button). */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0 mb-6">
            <div>
              <div className="skeleton h-6 w-32 mb-2"></div>
              <div className="skeleton h-4 w-64"></div>
            </div>
            <div className="skeleton h-10 w-full sm:w-52 rounded-lg"></div>
          </div>

          {/* Family members grid skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-lg border p-6">
                <div className="flex items-start mb-4">
                  <div className="skeleton-shimmer w-10 h-10 rounded-full mr-3"></div>
                  <div>
                    <div className="skeleton h-5 w-24 mb-2"></div>
                    <div className="skeleton h-3 w-16"></div>
                  </div>
                </div>
                <div className="space-y-2 mb-4">
                  <div className="skeleton h-4 w-full"></div>
                  <div className="skeleton h-4 w-3/4"></div>
                </div>
                <div className="flex justify-between items-center pt-4 border-t">
                  <div className="skeleton h-3 w-24"></div>
                  <div className="skeleton h-4 w-28"></div>
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0 mb-6">
            <div>
              <h2 className="text-xl font-semibold">Min familie</h2>
              <p className="text-text-secondary">Oversikt over familiemedlemmer og deres informasjon</p>
            </div>
            {/* Not offered while the list failed to load: the modal's
                already-in-the-family check needs the list. */}
            {!loadError && (
              <div className="flex space-x-3 w-full sm:w-auto">
                <button
                  onClick={() => setShowAddMember(true)}
                  className="w-full sm:w-auto px-4 py-2 min-h-[48px] bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors flex items-center justify-center"
                >
                  <PlusIcon className="w-5 h-5 mr-2" />
                  Legg til familiemedlem
                </button>
              </div>
            )}
          </div>

          {/* Family Members Grid. A failed load is an error, never "no family". */}
          {loadError ? (
            <div role="alert" className="text-center py-12">
              <p className="text-text-secondary mb-4">
                Vi fikk ikke hentet familien din akkurat nå.
              </p>
              <button
                type="button"
                onClick={() => refresh()}
                disabled={refreshing}
                className="px-6 py-3 min-h-[48px] bg-primary text-white rounded-lg font-semibold hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {refreshing ? 'Prøver igjen …' : 'Prøv igjen'}
              </button>
            </div>
          ) : familyMembers.length === 0 ? (
            <div className="text-center py-12">
              {/* 1.25 stroke: at w-16 the standard 1.75 renders visually heavy */}
              <UsersIcon className="w-16 h-16 mx-auto text-text-muted mb-4" strokeWidth={1.25} />
              <h3 className="text-lg font-medium text-text-primary mb-2">Ingen familiemedlemmer funnet</h3>
              <p className="text-text-muted mb-4">
                Begynn å legge til familiemedlemmer eller la Coord lære om familien din gjennom samtaler.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {familyMembers.map((member, index) => {
                const keyInfo = getKeyInfo(member.memories);
                const RelationIcon = getRelationshipIcon(member.name);

                return (
                  <div key={index} className="bg-white rounded-lg border p-6 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center">
                        <div className="w-10 h-10 rounded-full bg-sage-50 text-sage-700 flex items-center justify-center mr-3 flex-shrink-0">
                          <RelationIcon className="w-5 h-5" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-lg">{member.name}</h3>
                          <p className="text-sm text-text-muted">
                            {member.memories.length} minner
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Key Information */}
                    <div className="mb-4 space-y-2">
                      {keyInfo.age && (
                        <div className="flex items-center text-sm">
                          <CalendarIcon className="w-4 h-4 mr-2 text-text-muted" />
                          {keyInfo.age} år gammel
                        </div>
                      )}

                      {keyInfo.hasHealth && (
                        <div className="flex items-center text-sm">
                          <FileTextIcon className="w-4 h-4 mr-2 text-primary" />
                          Helseinformasjon
                        </div>
                      )}

                      {keyInfo.hasChallenges && (
                        <div className="flex items-center text-sm">
                          <AlertCircleIcon className="w-4 h-4 mr-2 text-warning" />
                          Utfordringer registrert
                        </div>
                      )}
                    </div>

                    {/* Footer */}
                    <div className="flex justify-between items-center pt-4 border-t">
                      <span className="text-xs text-text-muted">
                        Oppdatert {formatLastUpdated(member.lastUpdated)}
                      </span>
                      <Link
                        to="/account/coord-minner"
                        className="text-sm text-primary hover:text-primary-800"
                      >
                        Se alle minner →
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Help Text */}
          <div className="mt-8 p-4 bg-primary-50 rounded-lg border border-primary-200">
            <div className="flex items-start">
              <InfoIcon className="w-5 h-5 text-primary mt-0.5 mr-3 flex-shrink-0" />
              <div className="text-sm">
                <p className="text-primary-800 font-medium mb-1">Tips</p>
                <p className="text-primary-700">
                  Coord lærer automatisk om familien din gjennom samtaler. Du kan også legge til familiemedlemmer manuelt eller
                  se detaljerte minner om hver person under "Coord minner".
                </p>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
